<template>
    <div v-if="this.shouldBeVisible" class="trustpilot-popup-container">
        <modal
            ref="trustpilotModal"
            id="trustpilotPopup"
            size="modal-lg"
            :title="$t('trustpilotPopup.modalTitle')"
            :centered="true"
            :scrollable="true"
            :show="trustpilotModal.show"
            :close="trustpilotModal.hide"
            :closeable="trustpilotModal.closeable"
            :tighter-close-button="true"
            @modal-opening="onModalOpening"
            @modal-visible="onResize"
            @modal-close="onModalClosing"
        >
            <form
                method="post"
                action=""
                enctype="multipart/form-data"
                @submit="onFormSubmit($event)"
                class="cancel-order-popup-form"
                :style="formStyle"
                ref="cashbackForm"
                v-if="!trustpilotConfig.requestSent"
            >
                <div ref="trustpilotModalBody" class="modal-body" :style="bodyStyle">
                    <div>
                        <div
                            v-if="response.show && typeof response.message === 'string' && response.message.length > 0"
                        >
                            <div
                                class="alert"
                                :class="{
                                    'alert-success': response.success,
                                    'alert-danger': !response.success,
                                }"
                            >
                                <p class="mb-0">{{ response.message }}</p>
                            </div>
                        </div>
                        <div>
                            <p v-if="modalText !== null" v-html="modalText"></p>
                            <div class="form-group">
                                <label for="username">{{ $t('trustpilotPopup.username') }}</label>
                                <input
                                    type="text"
                                    name="username"
                                    id="username"
                                    placeholder=""
                                    required="required"
                                    class="form-control"
                                />
                                <div class="invalid-feedback"></div>
                            </div>
                            <div class="form-group">
                                <label for="sent_at">{{ $t('trustpilotPopup.date') }}</label>
                                <div class="datepicker-overlay">
                                    <nova-datepicker
                                        id="sent_at"
                                        name="sent_at"
                                        required="required"
                                        :mobileIcon="true"
                                        :config="datePickerOptions"
                                        v-model="sentAt"
                                        :class="{ 'is-invalid': formErrors.sent_at, 'form-control': true }"
                                    >
                                    </nova-datepicker>
                                </div>
                                <div class="invalid-feedback"></div>
                            </div>
                            <div class="text-center mt-1">
                                <button
                                    type="submit"
                                    style="font-size: 1rem"
                                    class="btn btn-primary btn-sm"
                                    :disabled="runningRequest"
                                >
                                    <i
                                        v-if="runningRequest"
                                        class="mdi mdi-loading mdi-spin"
                                        style="margin-right: 5px"
                                    ></i>
                                    <span v-html="$t('trustpilotPopup.sendRequest')"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <div class="modal-body" :style="bodyStyle" v-else>
                <div class="row">
                    <div class="col-6 col-md-2">
                        <i class="mdi mdi-check-circle-outline text-primary" style="font-size: 6rem"></i>
                    </div>
                    <div class="col-6 col-md-10 d-flex align-items-center">
                        <p v-html="$t('trustpilotPopup.requestSent')"></p>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        isPaymentConfirmed: {
            type: Boolean,
            required: true,
        },
        trustpilotConfig: {
            type: Object,
            required: true,
            default() {
                return {
                    invitationUri: null,
                    cashbackUri: null,
                    invitationSent: false,
                    requestSent: false,
                };
            },
        },
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            datePickerOptions: {
                defaultDate: new Date(),
                maxDate: new Date(),
                format: 'DD.MM.YYYY',
                locale: this.$language,
            },
            trustpilotModal: {
                show: false,
                hide: false,
                closeable: true,
            },
            csrfToken: null,
            formStyle: {
                height: '100%',
            },
            bodyStyle: {
                height: '100%',
                // marginTop: '-40px',
            },
            confirmError: false,
            confirm: false,
            formErrors: {},
            runningRequest: false,
            response: {
                show: false,
                success: false,
                message: null,
            },
            sentAt: '',
        };
    },

    computed: {
        /**
         * Determine if the setup popup of order should be visible.
         *
         * @returns {Boolean}
         */
        shouldBeVisible() {
            return this.isPaymentConfirmed;
        },

        /**
         * Load the modal text if defined.
         *
         * @returns {String|null}
         * */
        modalText() {
            const text = this.$t('trustpilotPopup.modalText');

            return text.trim().length > 0 ? text : null;
        },
    },

    methods: {
        /**
         * Handle the visibility update of the modal.
         *
         * @returns {this}
         */
        showModal() {
            this.trustpilotModal.show = true;
            this.trustpilotModal.hide = false;
            this.trustpilotModal.closeable = true;

            this.onShowModal();

            return this;
        },

        hideModal() {
            this.trustpilotModal.show = false;
            this.trustpilotModal.hide = true;
            this.trustpilotModal.closeable = true;

            return this;
        },

        /**
         * Handle the event callback of opening modal.
         *
         * @returns {void}
         */
        onModalOpening() {
            this.$el.ownerDocument.body.classList.add('overflow-hidden');
        },

        /**
         * Handle the event callback for closing modal.
         *
         * @returns {void}
         */
        onModalClosing() {
            this.$el.ownerDocument.body.classList.remove('overflow-hidden');
            this.hideModal();
        },

        /**
         * Handle the submit callback of form to send the expected request.
         *
         * @param {SubmitEvent} $event
         *
         * @returns {void}
         */
        onFormSubmit($event) {
            $event.preventDefault();

            // In the case the form was already submitted, we have to prevent another submit.
            if (this.runningRequest) {
                return;
            }

            // Update the is submitting flag and reset the response alert.
            this.runningRequest = true;
            this.formErrors = {};
            this.response.show = false;
            this.response.success = false;
            this.response.message = null;
            this.confirmError = false;

            // Prepare the request
            const formData = new FormData(this.$refs.cashbackForm);
            const uri = this.trustpilotConfig.cashbackUri;

            axios
                .post(uri, formData)
                .then((response) => {
                    // In the case the request was failed for some reason, we have to apply
                    // the message to our response property to show the user more details why
                    // the request was failed.
                    this.response.success = response.data.success;
                    this.response.message = response.data.message;

                    if (!this.response.success) {
                        // In the case that the response contains more details about the exact reason
                        // of failure, we have to provide them by adding these to the formErrors property.
                        if (typeof response.data.formErrors === 'object') {
                            // After the check above, we can assign the errors to the property.
                            this.formErrors = response.data.formErrors;
                        }

                        // Finally, disable the request blocking and show the response message in
                        // an alert container.
                        this.response.show = true;
                        this.runningRequest = false;

                        return;
                    }

                    // Show the success message and reload the page after 2 seconds, to assure
                    // we will show an updated page view with all necessary data.
                    // this.response.show = true;

                    this.trustpilotConfig.requestSent = true;
                    this.runningRequest = false;

                    // // Remove closeable
                    // this.trustpilotModal.closeable = false;
                    //
                    // setTimeout(() => {
                    //     window.location.href = location.href;
                    // }, 2000);
                })
                .catch(() => {
                    this.runningRequest = false;
                });
        },

        /**
         * Handle the callback of resize event, to assure the header size will be tracked correctly.
         *
         * @returns {void}
         */
        onResize() {},

        onShowModal() {
            // Don't send the invitation email when we already sent one or already received a cashback request.
            if (this.trustpilotConfig.requestSent || this.trustpilotConfig.invitationSent) return;

            const uri = this.trustpilotConfig.invitationUri;
            axios
                .post(uri)
                .then((response) => {
                    // In the case the request was failed for some reason, we have to apply
                    // the message to our response property to show the user more details why
                    // the request was failed.
                    this.response.success = response.data.success;
                    this.response.message = response.data.message;

                    if (!this.response.success) {
                        // Finally, disable the request blocking and show the response message in
                        // an alert container.
                        this.response.show = true;
                        this.runningRequest = false;
                    }
                })
                .catch(() => {
                    this.runningRequest = false;
                });
        },
    },

    beforeUnmount() {
        window.removeEventListener('resize', () => {
            this.onResize();
        });
    },

    beforeMount() {
        window.addEventListener('resize', () => {
            this.onResize();
        });
    },

    mounted() {
        this.hideModal();
    },
};
</script>
<style lang="scss">
.cancel-order-popup-container {
    @media screen and (max-width: 767px) {
        .modal-dialog {
            min-height: unset;
            max-height: unset;
            margin: 0;

            .modal-content {
                width: calc(100vw - 0.5rem);
            }
        }
    }
    @media screen and (min-width: 768px) {
        .horizontal-centered {
            margin: 0 auto;
        }
    }

    .modal-header {
        padding-bottom: 0;
    }
    .modal-body {
        padding-top: 0.5rem;
    }
}
</style>
